import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { apiUrl } from '../config/api';
import { useTheme } from '../context/ThemeContext';
import { useEnvironment } from '../hooks/useEnvironment';
import { formatResetInterval } from '../utils/dateUtils';

const PASSWORD_RULES = [
  { test: (p) => typeof p === 'string' && p.length >= 8, msg: 'At least 8 characters' },
  { test: (p) => /[A-Z]/.test(p), msg: 'One uppercase letter' },
  { test: (p) => /[a-z]/.test(p), msg: 'One lowercase letter' },
  { test: (p) => /[0-9]/.test(p), msg: 'One number' },
  { test: (p) => /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(p), msg: 'One special character' },
];

const Register = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const env = useEnvironment();
  const isDark = theme === 'dark';

  const [companyName, setCompanyName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Redirect away if self-registration is disabled in this environment.
  useEffect(() => {
    if (env && env.allowsSelfRegistration === false) {
      navigate('/login', { replace: true });
    }
  }, [env, navigate]);

  const failingRules = PASSWORD_RULES.filter(r => !r.test(password));
  const canSubmit = email.trim() && password && failingRules.length === 0 && !loading;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setError(null);
    setSuccess(null);
    setLoading(true);
    try {
      const res = await axios.post(apiUrl('/api/auth/register'), {
        companyName: companyName.trim(),
        email: email.trim(),
        password,
      });
      const baseMessage = res.data?.message || 'Account created. You can now sign in.';
      const slug = res.data?.slug;
      setSuccess(slug ? `${baseMessage} Your workspace URL: /${slug}` : baseMessage);
      // Land the user on their tenant-scoped login page if we got a slug back,
      // so the URL already reflects their workspace by the time they sign in.
      const target = slug ? `/${slug}/login` : '/login';
      setTimeout(() => navigate(target, { replace: true }), 1800);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden"
      style={{ background: 'linear-gradient(145deg, #060c1a 0%, #0d1f3c 30%, #162447 55%, #1a1a5e 80%, #2d1b69 100%)' }}
    >
      <div className="absolute pointer-events-none" style={{ top: '-10%', left: '-8%', width: 600, height: 600, borderRadius: '50%', background: 'radial-gradient(circle, rgba(59,130,246,0.18) 0%, transparent 65%)' }} />
      <div className="absolute pointer-events-none" style={{ bottom: '-12%', right: '-8%', width: 650, height: 650, borderRadius: '50%', background: 'radial-gradient(circle, rgba(99,102,241,0.2) 0%, transparent 65%)' }} />

      <div className="w-full max-w-[460px] relative z-10">
        <div
          className="rounded-3xl overflow-hidden"
          style={{
            background: isDark ? 'rgba(15,23,42,0.97)' : 'rgba(255,255,255,0.985)',
            boxShadow: '0 40px 80px -16px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.07)',
          }}
        >
          <div className="h-[3px]" style={{ background: 'linear-gradient(90deg, #3b82f6 0%, #6366f1 40%, #8b5cf6 70%, #a855f7 100%)' }} />

          <div className="px-8 pt-8 pb-3 text-center">
            <h2
              className="text-[22px] font-bold tracking-tight"
              style={{
                background: isDark ? 'linear-gradient(135deg, #93c5fd, #818cf8)' : 'linear-gradient(135deg, #1e3a8a, #4f46e5)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              Register your company
            </h2>
            <p className="mt-1.5 text-sm text-gray-500 dark:text-slate-400 font-medium">
              {env?.demo
                ? 'Try the system in our public demo sandbox.'
                : 'Spin up a fresh sandbox in this environment.'}
            </p>
            {env?.demo && (
              <p className="mt-2 text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/40 rounded-xl py-2 px-3">
                Demo data is wiped every {formatResetInterval(env.resetIntervalHours)}.
              </p>
            )}
          </div>

          <form onSubmit={handleSubmit} className="px-8 pb-8 space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-800 dark:text-slate-200 mb-1">Company name (optional)</label>
              <input
                type="text"
                value={companyName}
                onChange={e => setCompanyName(e.target.value)}
                maxLength={120}
                className="w-full"
                placeholder="Acme Microloans"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-800 dark:text-slate-200 mb-1">Email address</label>
              <input
                type="email"
                required
                value={email}
                onChange={e => setEmail(e.target.value)}
                className={`w-full ${email ? '' : 'border-rose-300 dark:border-rose-700/60 focus:border-rose-500 focus:ring-rose-500/20'}`}
                placeholder="owner@yourcompany.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-800 dark:text-slate-200 mb-1">Create password</label>
              <div className={`flex items-center bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-500 rounded transition-colors focus-within:ring-2 ${password ? 'border-emerald-300 dark:border-emerald-700/60 focus-within:border-emerald-500 focus-within:ring-emerald-500/20' : 'border-rose-300 dark:border-rose-700/60 focus-within:border-rose-500 focus-within:ring-rose-500/20'}`}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className="flex-1 bg-transparent"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(v => !v)}
                  className="pr-3 text-xs font-semibold text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300"
                  tabIndex={-1}
                >
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              </div>

              {password.length > 0 && (
                <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-1 text-[11px]">
                  {PASSWORD_RULES.map(rule => {
                    const passes = rule.test(password);
                    return (
                      <li
                        key={rule.msg}
                        className={`flex items-center gap-1.5 ${passes ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-500 dark:text-slate-400'}`}
                      >
                        <span aria-hidden>{passes ? '✓' : '•'}</span>
                        <span>{rule.msg}</span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            {error && (
              <div className="flex items-start gap-2.5 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/40 rounded-xl">
                <span className="text-red-600 dark:text-red-400 text-sm font-medium">{error}</span>
              </div>
            )}
            {success && (
              <div className="flex items-start gap-2.5 p-3 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800/40 rounded-xl">
                <span className="text-emerald-700 dark:text-emerald-400 text-sm font-medium">{success}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={!canSubmit}
              className="w-full text-white py-3 px-4 rounded-xl font-semibold text-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 transition-all"
              style={{
                background: 'linear-gradient(135deg, #2563eb 0%, #4f46e5 50%, #7c3aed 100%)',
                boxShadow: canSubmit ? '0 4px 20px rgba(79,70,229,0.45)' : 'none',
              }}
            >
              {loading ? 'Creating account…' : 'Create account'}
            </button>

            <div className="text-center text-sm">
              <span className="text-gray-500 dark:text-slate-400">Already have an account? </span>
              <Link to="/login" className="font-semibold text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300">
                Sign in
              </Link>
            </div>
          </form>
        </div>

        <p className="mt-6 text-center text-xs" style={{ color: 'rgba(255,255,255,0.3)' }}>
          {env?.environment ? `${env.environment.toUpperCase()} environment` : 'Sandbox environment'}
        </p>
      </div>
    </div>
  );
};

export default Register;
